import { Button } from '@massilia/ui'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  CalendarClock,
  Hourglass,
  RefreshCw,
  Search,
  ShieldCheck,
  Wrench,
} from 'lucide-react'

import { isChatWidgetEnabled } from '@/lib/feature-flags'

interface FlightErrorCardProps {
  onRetry?: () => void
  type?: FlightErrorType
}

type FlightErrorType =
  | 'error'
  | 'maintenance'
  | 'no-results'
  | 'rate-limited-day'
  | 'rate-limited-minute'

const ERROR_ICONS = {
  'error': AlertTriangle,
  'maintenance': Wrench,
  'no-results': Search,
  'rate-limited-day': CalendarClock,
  'rate-limited-minute': Hourglass,
} satisfies Record<FlightErrorType, typeof AlertTriangle>

const ERROR_TITLES = {
  'error': 'Erreur de recherche',
  'maintenance': 'Maintenance en cours',
  'no-results': 'Aucun résultat trouvé',
  'rate-limited-day': 'Limite quotidienne atteinte',
  'rate-limited-minute': 'Petite pause nécessaire',
} satisfies Record<FlightErrorType, string>

const ERROR_DESCRIPTIONS = {
  'error':
    'Une erreur est survenue lors de la recherche de vols. Cela peut être dû à un problème temporaire.',
  'maintenance':
    'Notre partenaire de réservation de vols est temporairement indisponible. La recherche de vols devrait revenir très prochainement.',
  'no-results':
    "Nous n'avons pas pu trouver de vols correspondant à votre recherche. Essayez avec des dates différentes ou une destination alternative.",
  'rate-limited-day':
    "Vous avez atteint le nombre maximum de recherches autorisées pour aujourd'hui. Le compteur sera réinitialisé à minuit (heure de Paris).",
  'rate-limited-minute':
    'Pour des raisons de sécurité, nous limitons le nombre de recherches par minute. Merci de patienter un court instant avant de relancer votre recherche.',
} satisfies Record<FlightErrorType, string>

export function FlightErrorCard({ onRetry, type = 'no-results' }: FlightErrorCardProps) {
  const isError = type === 'error'
  const isMaintenance = type === 'maintenance'
  const isRateLimitedMinute = type === 'rate-limited-minute'
  const isRateLimitedDay = type === 'rate-limited-day'
  const isRateLimited = isRateLimitedMinute || isRateLimitedDay
  const isProblem = isError || isMaintenance || isRateLimited
  // Per-day rate-limit: retrying is useless until tomorrow's reset, hide it.
  const showRetry = !!onRetry && !isRateLimitedDay
  const ErrorIcon = ERROR_ICONS[type]

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white px-8 py-12 shadow-sm">
      <div
        className={`mb-6 flex h-20 w-20 items-center justify-center rounded-full ${isProblem ? 'bg-red-50' : 'bg-amber-50'}`}
      >
        <ErrorIcon className={`h-10 w-10 ${isProblem ? 'text-red-500' : 'text-amber-500'}`} />
      </div>

      <h2 className="mb-2 text-xl font-semibold text-gray-900">{ERROR_TITLES[type]}</h2>

      <p className="mb-6 max-w-md text-center text-gray-600">{ERROR_DESCRIPTIONS[type]}</p>

      <div className={`mb-8 rounded-lg p-4 ${isProblem ? 'bg-red-50' : 'bg-sky-50'}`}>
        <p className={`mb-3 text-sm font-medium ${isProblem ? 'text-red-700' : 'text-sky-700'}`}>
          {isProblem ? 'Que faire ?' : 'Quelques conseils :'}
        </p>
        <ul className={`space-y-2 text-sm ${isProblem ? 'text-red-600' : 'text-sky-600'}`}>
          {getErrorTips(type)}
        </ul>
      </div>

      <div className="flex gap-3">
        {showRetry && (
          <Button
            className={
              isProblem
                ? 'border-red-200 text-red-600 hover:bg-red-50'
                : 'border-sky-200 text-sky-600 hover:bg-sky-50'
            }
            onClick={onRetry}
            variant="outline"
          >
            <RefreshCw className="size-4" />
            Réessayer
          </Button>
        )}
        {/* `/` est l'accueil de l'espace connecté : un visiteur non authentifié y était
            redirigé vers /connexion au lieu du formulaire de recherche. */}
        <Link to="/recherche">
          <Button className="bg-brand-600 hover:bg-brand-700">Nouvelle recherche</Button>
        </Link>
      </div>
    </div>
  )
}

function getErrorTips(type: FlightErrorType) {
  if (type === 'maintenance') {
    return (
      <>
        <li className="flex items-start gap-2">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Réessayez dans quelques minutes</span>
        </li>
        <li className="flex items-start gap-2">
          <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Le service est en cours de rétablissement, aucune action n&apos;est requise de votre
            part
          </span>
        </li>
      </>
    )
  }

  if (type === 'rate-limited-day') {
    return (
      <>
        <li className="flex items-start gap-2">
          <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Revenez demain : le quota est remis à zéro à minuit (heure de Paris)</span>
        </li>
        <li className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Cette limite quotidienne protège le service contre les abus, elle s&apos;applique à tous
            les visiteurs
          </span>
        </li>
      </>
    )
  }

  if (type === 'rate-limited-minute') {
    return (
      <>
        <li className="flex items-start gap-2">
          <Hourglass className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Patientez environ une minute avant de relancer la recherche</span>
        </li>
        <li className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            Cette limite protège le service contre les abus, elle s&apos;applique à tous les
            visiteurs
          </span>
        </li>
      </>
    )
  }

  if (type === 'error') {
    return (
      <>
        <li className="flex items-start gap-2">
          <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Réessayez dans quelques instants</span>
        </li>
        <li className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Si le problème persiste, contactez le support</span>
        </li>
      </>
    )
  }

  return (
    <>
      <li className="flex items-start gap-2">
        <span>📅</span>
        <span>Essayez des dates plus flexibles (quelques jours d&apos;écart)</span>
      </li>
      <li className="flex items-start gap-2">
        <span>✈️</span>
        <span>Vérifiez que les villes de départ et d&apos;arrivée sont correctes</span>
      </li>
      {isChatWidgetEnabled && (
        <li className="flex items-start gap-2">
          <span>💬</span>
          <span>Utilisez le chatbot pour affiner votre recherche avec notre aide</span>
        </li>
      )}
    </>
  )
}
